import _ from 'lodash';
import server from 'common/utils/server';
import { getSearchFilter } from 'common/utils/locationUtils';
import { DateTime, shortUsaDateFormat } from 'common/utils/date';

const initialState = {
  reviewsById: {},
  reviewsByCompletedReviewsTablePage: {},
  totalCompletedReviewsTablePages: null,
  totalReviewCount: null,
  totalAnnotations: null,
  averageScore: null,
  employeeCount: null,
  reviewedEmployeeCount: null,
  scoredReviewCount: null,
  reviewsWithOnlyAnnotationsCount: null,
  appealedReviewsCount: null,
  lastFetchQueryParams: {},
  completedReviewsTableSortedBy: null,
  completedReviewsPageSortDirection: null,
  tableViewPage: 1,
  revised: false,
};

export function completedReviewState(state = initialState, action) {
  switch (action.type) {
    case 'qa_reviews/SET_TABLE_VIEW_PAGE': {
      const { tableViewPage } = action;
      return { ...state, tableViewPage };
    }
    case 'qa_reviews/RECEIVED_REVIEWS': {
      const { reviews, page, totalCompletedReviewsTablePages, totalReviewCount, queryParams } =
        action.value;
      const reviewsById = { ...state.reviewsById };
      reviews.forEach(review => {
        reviewsById[review.id] = review;
      });

      let reviewsByCompletedReviewsTablePage = {
        ...state.reviewsByCompletedReviewsTablePage,
      };
      // if any of the query params have changed (besides page),
      // clear out reviewsByCompletedReviewsTablePage
      const oldParams = { ...state.lastFetchQueryParams };
      delete oldParams.page;
      const newParams = { ...queryParams };
      delete newParams.page;
      if (!_.isEqual(oldParams, newParams)) {
        reviewsByCompletedReviewsTablePage = {};
      }
      reviewsByCompletedReviewsTablePage[page] = reviews;
      return {
        ...state,
        reviewsById,
        reviewsByCompletedReviewsTablePage,
        totalCompletedReviewsTablePages,
        lastFetchQueryParams: queryParams,
        totalReviewCount,
      };
    }
    case 'qa_reviews/RECEIVED_TOTALS_AND_OVERVIEW': {
      const {
        totalReviewCount,
        averageScore,
        employeeCount,
        reviewedEmployeeCount,
        scoredReviewCount,
        reviewsWithOnlyAnnotationsCount,
        appealedReviewsCount,
        totalAnnotations,
      } = action.value;
      return {
        ...state,
        totalReviewCount,
        averageScore,
        employeeCount,
        reviewedEmployeeCount,
        scoredReviewCount,
        reviewsWithOnlyAnnotationsCount,
        appealedReviewsCount,
        totalAnnotations,
      };
    }
    case 'qa_reviews/CLICKED_COMPLETED_REVIEW_TABLE_HEADER': {
      if (!state.totalCompletedReviewsTablePages) {
        return state;
      }
      const { sortBy } = action;
      const { completedReviewsTableSortedBy } = state;
      let { completedReviewsPageSortDirection } = state;
      if (sortBy === completedReviewsTableSortedBy) {
        completedReviewsPageSortDirection =
          completedReviewsPageSortDirection === 'ascending' ? 'descending' : 'ascending';
      } else {
        const columnsWithDescendingDefaultSort = ['interaction_id'];
        completedReviewsPageSortDirection =
          columnsWithDescendingDefaultSort.includes(sortBy) ? 'descending' : 'ascending';
      }
      return {
        ...state,
        completedReviewsTableSortedBy: sortBy,
        completedReviewsPageSortDirection,
      };
    }
    case 'qa_reviews/RESET': {
      return {
        ...state,
        reviewsById: {},
        reviewsByCompletedReviewsTablePage: {},
        totalCompletedReviewsTablePages: null,
        lastFetchQueryParams: {},
        tableViewPage: 1,
      };
    }
    default: {
      return state;
    }
  }
}

export const getCompletedReviewsQuery = (
  history,
  { customProperties, snapshotFilters = {}, interactionSnapshotFiltersEnabled = false } = {}
) => {
  const {
    startDate,
    startDateTime,
    endDate,
    endDateTime,
    teamIds,
    groupIds,
    reportingGroupIds,
    employeeIds,
    reviewerIds,
    qaFormIds,
    autofailed,
    languages,
    channelEnumIds,
    origins,
    propertyIds,
    reviewEvents,
    assignmentIds,
    includeAudited,
    qaOptionId,
    qaQuestionId,
    countTowardScore,
  } = getSearchFilter(history);

  return {
    completed_from: startDate,
    completed_to: endDate,
    completed: true,
    by_reviewer_id: reviewerIds,
    by_employee_id: employeeIds,
    for_teams: teamIds,
    for_groups: groupIds,
    for_reporting_groups: reportingGroupIds,
    by_qa_assignment_id: assignmentIds,
    by_qa_form_id: qaFormIds,
    ...(!interactionSnapshotFiltersEnabled && { by_language: languages }),
    by_channel_enum_id: channelEnumIds,
    by_origin: origins,
    by_property_id: propertyIds,
    by_resolution_status: reviewEvents,
    excluding_audited: includeAudited === false || undefined,
    ...(countTowardScore ? { count_toward_score: true } : {}),
    ...(qaOptionId ? { qa_option_id: qaOptionId } : {}),
    ...(qaQuestionId ? { qa_question_id: qaQuestionId } : {}),
    ...(startDateTime && endDateTime ?
      {
        completed_from_dt: startDateTime,
        completed_to_dt: endDateTime,
      }
    : {}),
    autofailed,
    ...(customProperties ? { custom_properties: customProperties } : {}),
    ...snapshotFilters,
  };
};

export const loadCompletedReviews = params => async (dispatch, getState) => {
  const queryParams = { ...params };
  const { completedReviewState } = getState();
  const { completedReviewsTableSortedBy, completedReviewsPageSortDirection, tableViewPage } =
    completedReviewState;
  queryParams.page = tableViewPage;

  if (completedReviewsTableSortedBy) {
    queryParams.order_by = completedReviewsTableSortedBy;
    queryParams.order_direction =
      completedReviewsPageSortDirection === 'descending' ? 'desc' : 'asc';
  }
  const { qa_reviews, total_count, total_pages } = await server.get(
    '/api/v1/qa_reviews',
    queryParams
  );
  dispatch({
    type: 'qa_reviews/RECEIVED_REVIEWS',
    value: {
      reviews: qa_reviews
        .map(review => {
          if (!review.employee || !review.reviewer) {
            return null;
          }

          return {
            id: review.id,
            ...(review.audit ? { audit: review.audit } : {}),
            ...(review.auditor ? { auditor: review.auditor } : {}),
            scorecardName: review.qa_form_name,
            annotationsCount: review.qa_annotations_count,
            employeeName: `${review.employee.first_name} ${review.employee.last_name}`,
            reviewerName: `${review.reviewer.first_name} ${review.reviewer.last_name}`,
            reviewerImage: review.reviewer.image,
            interactionId: review.interaction_id,
            completedAt: review.completed_at ?? '(not completed)',
            percentageScore: review.count_toward_score ? review.percentage_score : null,
            employeeImage: review.employee.image,
            primaryMetricValue: review.primary_metric_value,
            hideReviewer: review.hide_reviewer,
            resolutionStatus: review.resolution_status,
            revised: review.revised,
          };
        })
        .filter(Boolean),
      page: tableViewPage,
      totalCompletedReviewsTablePages: total_pages,
      totalReviewCount: total_count,
      queryParams,
    },
  });
};

let lastDispatchedId = null;
export const loadTeamTotalsAndOverview =
  (
    history,
    filterOverrides = {},
    { customProperties, snapshotFilters = {}, interactionSnapshotFiltersEnabled = false } = {}
  ) =>
  async dispatch => {
    const filters = getSearchFilter(history);
    const {
      startDate,
      startDateTime,
      endDate,
      endDateTime,
      teamIds,
      groupIds,
      reportingGroupIds,
      employeeIds,
      reviewerIds,
      qaFormIds,
      autofailed,
      languages,
      channelEnumIds,
      origins,
      propertyIds,
      reviewEvents,
      assignmentIds,
      includeAudited,
    } = { ...filters, ...filterOverrides };

    const resolvedCustomProperties = customProperties || filterOverrides.custom_properties;
    const queryParams = {
      completed_from: startDate,
      completed_to: endDate,
      by_reviewer_id: reviewerIds,
      by_employee_id: employeeIds,
      for_teams: teamIds,
      for_groups: groupIds,
      for_reporting_groups: reportingGroupIds,
      by_qa_assignment_id: assignmentIds,
      by_qa_form_id: qaFormIds,
      ...(!interactionSnapshotFiltersEnabled && { by_language: languages }),
      by_channel_enum_id: channelEnumIds,
      by_origin: origins,
      by_property_id: propertyIds,
      by_resolution_status: reviewEvents,
      excluding_audited: includeAudited === false || undefined,
      ...(resolvedCustomProperties ? { custom_properties: resolvedCustomProperties } : {}),
      ...(startDateTime && endDateTime ?
        {
          completed_from_dt: startDateTime,
          completed_to_dt: endDateTime,
        }
      : {}),
      autofailed,
      ...snapshotFilters,
    };
    const thisDispatchId = _.uniqueId();
    lastDispatchedId = thisDispatchId;
    const [
      { average_score, employees_count, reviewed_employees_count, total_annotations },
      { total_reviews, reviews_scored, annotations_only, appealed_count },
    ] = await Promise.all([
      server.get('/api/v1/qa_reporting/team_overview', queryParams),
      server.get('/api/v1/qa_reporting/total_reviews', queryParams),
    ]);
    if (lastDispatchedId === thisDispatchId) {
      dispatch({
        type: 'qa_reviews/RECEIVED_TOTALS_AND_OVERVIEW',
        value: {
          totalReviewCount: total_reviews,
          averageScore: average_score,
          employeeCount: employees_count,
          reviewedEmployeeCount: reviewed_employees_count,
          scoredReviewCount: reviews_scored,
          reviewsWithOnlyAnnotationsCount: annotations_only,
          appealedReviewsCount: appealed_count,
          totalAnnotations: total_annotations,
        },
      });
    }
  };

export const clickCompletedReviewTableHeader =
  ({ history, sortBy, metadataFilters = {} }) =>
  dispatch => {
    dispatch({
      type: 'qa_reviews/CLICKED_COMPLETED_REVIEW_TABLE_HEADER',
      sortBy,
    });

    const query = getCompletedReviewsQuery(history, metadataFilters);
    dispatch(loadCompletedReviews(query));
    dispatch(loadTeamTotalsAndOverview(history, {}, metadataFilters));
  };
